import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import ComparisonPill from 'src/components/dashboard/DashboardPage/components/ComparisonPill/ComparisonPill';
import useNpsScoreColor from 'src/components/dashboard/hooks/useNpsScoreColor';

// Half a circle
const GAUGE_RADIUS = 46;
const GAUGE_CIRCUMFERENCE = 0.5 * 2 * GAUGE_RADIUS * Math.PI;
const GAUGE_SVG_SIZE = 268;
const GAUGE_STROKE_WIDTH = 8;

interface GaugeProps {
  score: number;
  min?: number;
  max?: number;
  neutralScore?: number;
  comparisonScore?: string;
}

const Gauge: FC<GaugeProps> = ({
  score,
  min = -100,
  max = 100,
  neutralScore = 0,
  comparisonScore,
}) => {
  const { t } = useTranslation('followUp');
  const [strokeDashArray, setStrokeDashArray] = useState('0, 999');
  const [rotation, setRotation] = useState(0);
  const theme = useTheme();

  // percentage of the score between min and max,
  // used to calculate the strokeDashArray and the rotation of the triangle
  const percentage = useMemo(
    () => ((score - min) / (max - min)) * 100,
    [score, min, max],
  );

  useEffect(() => {
    // set states to trigger the animations
    setStrokeDashArray(`${(percentage / 100) * GAUGE_CIRCUMFERENCE}, 999`);
    setRotation((percentage / 100) * 180);
  }, [percentage]);

  const stroke = useNpsScoreColor()(score, neutralScore);

  return (
    <Box
      sx={{
        pt: 3.25,
      }}
    >
      <Box
        sx={{
          mx: 'auto',
          width: GAUGE_SVG_SIZE,
          position: 'relative',
        }}
      >
        <svg viewBox="0 0 100 55" width="100%" height="auto">
          <g fill="none" strokeWidth={GAUGE_STROKE_WIDTH} strokeLinecap="round">
            {/* background */}
            <g
              strokeDasharray={`${GAUGE_CIRCUMFERENCE}, 999`}
              transform="rotate(180, 50, 50)"
              stroke={theme.colors.neutral[100]}
            >
              <circle cx="50" cy="50" r={GAUGE_RADIUS} />
            </g>
            {/* foreground */}
            <g
              transform="rotate(180, 50, 50)"
              fill="none"
              strokeDasharray={strokeDashArray}
              stroke={stroke}
              style={{
                transition: 'all 0.5s ease-out',
              }}
            >
              <circle cx="50" cy="50" r={GAUGE_RADIUS} />
            </g>
          </g>
        </svg>

        {/* triangle */}
        <Box
          sx={{
            position: 'absolute',
            width: '50%',
            height: '1px',
            left: '0',
            bottom: `calc(5% + ${GAUGE_STROKE_WIDTH}px)`,
            transform: `rotate(${rotation}deg)`,
            transformOrigin: '100% 100%',
            transition: 'all 0.5s ease-out',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21"
            height="24"
            viewBox="0 0 21 24"
            fill="none"
            style={{
              position: 'absolute',
              bottom: '0%',
              right: '100%',
              transform: 'translate(-25%, 50%)',
            }}
          >
            <path
              // eslint-disable-next-line max-len
              d="M0.144375 1.75158C0.144375 0.981781 0.977708 0.500657 1.64437 0.885557L19.6444 11.2779C20.311 11.6628 20.311 12.625 19.6444 13.0099L1.64437 23.4022C0.977708 23.7871 0.144375 23.306 0.144375 22.5362L0.144375 1.75158Z"
              fill="#1F2937"
            />
          </svg>
        </Box>

        <Stack
          direction="column"
          sx={{
            width: `${GAUGE_SVG_SIZE - 70}px`,
            justifyContent: 'flex-end',
            gap: '1rem',
            position: 'absolute',
            bottom: '0',
            textAlign: 'center',
            left: '50%',
            transform: 'translate(-50%, 0)',
          }}
        >
          <Typography
            variant="body3"
            sx={{
              fontWeight: '600',
            }}
          >
            {neutralScore}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '2.75rem',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            {score}
            {comparisonScore && (
              <ComparisonPill
                value={comparisonScore}
                component="span"
                sx={{
                  ml: 0.5,
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              />
            )}
          </Typography>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="body3"
              sx={{
                fontWeight: 600,
              }}
            >
              {min}
            </Typography>
            <Typography
              variant="body3"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('npsScore')}
            </Typography>
            <Typography
              variant="body3"
              sx={{
                fontWeight: 600,
              }}
            >
              {max}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default Gauge;
